/**
 * Every `import.meta.env` read in the application happens here.
 *
 * The values are written into `frontend/.env.<mode>` by `scripts/deploy.sh` from the CDK
 * stack outputs, so no environment-specific URL is ever hand-maintained or committed.
 */

export interface AppFeatureFlags {
  recipeSubmission: boolean;
  ordering: boolean;
}

export interface RuntimeConfig {
  apiUrl: string;
  appUrl: string;
  environment: string;
  version: string;
  featureFlags: AppFeatureFlags;
}

const DEFAULT_FLAGS: AppFeatureFlags = {
  recipeSubmission: true,
  ordering: false,
};

function parseFeatureFlags(raw: string | undefined): AppFeatureFlags {
  if (!raw) return DEFAULT_FLAGS;

  try {
    const parsed = JSON.parse(raw) as Partial<AppFeatureFlags>;
    return {
      recipeSubmission: parsed.recipeSubmission ?? DEFAULT_FLAGS.recipeSubmission,
      ordering: parsed.ordering ?? DEFAULT_FLAGS.ordering,
    };
  } catch {
    // A malformed blob must not blank the site — fall back and carry on.
    console.warn('VITE_FEATURE_FLAGS_JSON could not be parsed; using defaults');
    return DEFAULT_FLAGS;
  }
}

let cached: RuntimeConfig | undefined;

export function getRuntimeConfig(): RuntimeConfig {
  cached ??= {
    // Trailing slashes are stripped so `${apiUrl}/recipes` never becomes a double slash.
    apiUrl: (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, ''),
    appUrl: import.meta.env.VITE_APP_URL ?? '',
    environment: import.meta.env.VITE_ENVIRONMENT ?? 'local',
    version: import.meta.env.VITE_APP_VERSION ?? 'dev',
    featureFlags: parseFeatureFlags(import.meta.env.VITE_FEATURE_FLAGS_JSON),
  };

  return cached;
}

/** Test seam — the config is memoised for the life of the page. */
export function resetRuntimeConfig(): void {
  cached = undefined;
}

/** True on anything that is not production, used to show the environment banner. */
export const isNonProduction = (): boolean =>
  !['prod', 'production'].includes(getRuntimeConfig().environment);
